import { useState } from "react";
import { Eye, Loader2, SquarePlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import PageContainer from "./containers/page-container";
import ImagePicker from "./components/shared/image-picker";
import { ImageCarousel } from "./components/shared/image-carousel";
import type {
  BalanceBillListResponseDataType,
  SelectedImageObjectType,
} from "@/types";
import {
  useBalanceBillItemDetails,
  useBalanceBillList,
} from "@/hooks/use-balance-bill";
import useUserInfo from "@/hooks/use-user-info";
import BalanceBillSkeletonLoader from "./components/balance-bill/balance-bill-list-skeleton-loader";
import BalanceBillUploadDialog from "./components/balance-bill/balance-bill-upload-dialog";
import type { BalanceBillItemRequest } from "@/model/balance-bill";
import BalanceBillDocumentPreviewDialog from "./components/balance-bill/balance-bill-document-preview";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { queryClient } from "@/hooks/use-query-client";
import { QueryKeys } from "@/constants";
import { Navigate } from "react-router-dom";
import { ACCEPTED_BALANCE_BILL_FILE_TYPES } from "@/lib/balance-bill-utils";
import type { MESSAGE_ICON_TYPE } from "@/constants/message-icon";
import { MESSAGE_ICON } from "@/constants/message-icon";
import { AppAlertWithIconDialog } from "./components/app-dialog/app-dialog-with-icon";

const BalanceBill = () => {
  const userInfo = useUserInfo();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  const balanceBillItemRequest: Omit<BalanceBillItemRequest, "ticketId"> = {
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    claimCoverageCode: activeCoverage?.claimCoverageCode,
    planId: userInfo?.planId,
    token: userInfo?.accessToken,
  };

  const {
    data: balanceBillList,
    isFetching: isBalanceBillListFetching,
    isLoading: isBalanceBillListLoading,
    isFetched: isBalanceBillListFetched,
  } = useBalanceBillList();

  const {
    mutate,
    data: balanceBillItemResponseData,
    isPending: isBalanceBillItemResponsePending,
  } = useBalanceBillItemDetails();

  const [selectedImages, setSelectedImages] = useState<
    SelectedImageObjectType[]
  >([]);
  const [isSelectedImagesPreviewOpen, setIsSelectedImagesPreviewOpen] =
    useState<boolean>(false);
  // const [selectedBill, setSelectedBill] = useState<
  //   Omit<SelectedImageObjectType, "image">[]
  // >([]);
  // const [isSelectedBillPreviewOpen, setIsSelectedBillPreviewOpen] =
  //   useState<boolean>(false);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const [dialogOpen, setIsDialogOpen] = useState({
    iconType: MESSAGE_ICON.ERROR as MESSAGE_ICON_TYPE,
    open: false,
    message: "Something went wrong",
    subMessage: "Please try again later.",
  });
  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    useState<boolean>(false);
  const [billItemDialogOpen, setBillItemDialogOpen] = useState(false);

  const showBalanceBill = queryClient.getQueryData([
    QueryKeys.GLOBAL_FLAGS,
    userInfo.username,
  ])?.data?.[0]?.showBilling;

  const onSelectedImagePreviewClose = () => {
    setIsSelectedImagesPreviewOpen(false);
    setSelectedImages([]);
  };

  // const onSelectedBillPreviewClose = () => {
  //   setIsSelectedBillPreviewOpen(false);
  //   setSelectedBill([]);
  // };

  const onSelectedImagePreviewConfirm = () => {
    setIsSelectedImagesPreviewOpen(false);
    setConfirmationDialogOpen(true);
  };

  const onImageSelect = (imageFiles: File[]) => {
    const imageObjects = imageFiles.map((file) => ({
      imageUrl: URL.createObjectURL(file),
      image: file,
    }));
    setSelectedImages(imageObjects);
    setCarouselStartIndex(0);
    setIsSelectedImagesPreviewOpen(true);
  };

  const onViewBalanceBill = (
    bill: BalanceBillListResponseDataType,
    index: number,
  ) => {
    setCarouselStartIndex(index);

    mutate(
      {
        ...balanceBillItemRequest,
        ticketId: bill?.ticketId,
      },
      {
        onSuccess: () => {
          setBillItemDialogOpen(true);
        },
      },
    );
  };

  if (!showBalanceBill) return <Navigate to="/" />;

  return (
    <>
      <PageContainer title="Balance Billing">
        <div className="min-h-screen bg-background">
          <div className="space-y-8">
            {/* Header Section */}
            <div className="mx-6 mt-6 space-y-6">
              <div className="space-y-4">
                <p className="text-sm leading-relaxed text-foreground lg:text-[0.9rem]">
                  A balance bill, or surprise billing, is a medical bill from a
                  healthcare provider to you for the difference between the
                  amount the insurance will pay and the total cost of the
                  services being charged.
                </p>
              </div>

              <ImagePicker
                maxFiles={5}
                allowedTypes={ACCEPTED_BALANCE_BILL_FILE_TYPES}
                onImageSelect={(files) => onImageSelect(files)}
                onValidationError={(error) => {
                  setIsDialogOpen({
                    iconType: MESSAGE_ICON.WARNING,
                    open: true,
                    message: error.message,
                    subMessage: error.subMessage,
                  });
                }}
                trigger={
                  <div className="flex max-h-52 cursor-pointer items-center justify-center rounded-sm border-2 border-dashed border-gray-300 px-4 py-8 text-gray-400 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900">
                    <div className="h-full space-y-3 text-center">
                      <SquarePlus
                        height={40}
                        width={40}
                        strokeWidth={1}
                        className="inline text-primary"
                      />
                      <p className="text-foreground/20 text-sm leading-relaxed lg:text-[0.9rem]">
                        Upload a photo or documentation of any information
                        related to your balance bill for research and
                        resolution.
                      </p>
                      <p className="text-foreground/20 text-sm leading-relaxed lg:text-[0.9rem]">
                        Accepted types: JPG, JPEG, PNG, PDF. <br />
                        Max 5 files & Max 10 MB each.
                      </p>
                    </div>
                  </div>
                }
              />
            </div>
            {/* Submitted Bills Section */}
            <div className="mx-6 bg-muted p-4 text-center">
              <div className="text-lg font-medium text-gray-800">
                Submitted Bills
              </div>
            </div>
            {isBalanceBillListFetching || isBalanceBillListLoading ? (
              <BalanceBillSkeletonLoader />
            ) : null}
            {balanceBillList?.length === 0 ? (
              <div className="mt-8 flex justify-center">
                <div className="text-sm leading-relaxed text-muted-foreground lg:text-[0.9rem]">
                  No balance bills have been submitted.
                </div>
              </div>
            ) : null}
            {isBalanceBillListFetched &&
            balanceBillList &&
            balanceBillList?.length > 0 ? (
              <div className="bg-muted/50 mx-6">
                <div className="space-y-0 overflow-hidden rounded-md bg-card">
                  {balanceBillList?.map(
                    (bill: BalanceBillListResponseDataType, i: number) => (
                      <div
                        key={`balance-bill-item-${i}`}
                        className="hover:bg-muted/30 } flex items-center justify-between border-b border-border p-4 transition-colors"
                      >
                        {bill?.submittedAt && (
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground lg:text-[0.9rem]">
                              Submitted: {bill?.submittedAt}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            className="border-primary text-primary"
                            size="lg"
                            disabled={
                              isBalanceBillItemResponsePending ? true : false
                            }
                            onClick={() => onViewBalanceBill(bill, i)}
                          >
                            {isBalanceBillItemResponsePending &&
                            carouselStartIndex === i ? (
                              <>
                                Loading...
                                <Loader2 className="mr-2 size-4 animate-spin" />
                              </>
                            ) : (
                              <>
                                <Eye height={10} /> View
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </PageContainer>
      {/* Preview for images to upload */}
      <ImageCarousel
        images={selectedImages}
        isOpen={isSelectedImagesPreviewOpen}
        onClose={onSelectedImagePreviewClose}
        onConfirm={onSelectedImagePreviewConfirm}
        initialIndex={carouselStartIndex}
      />
      {/* Preview for uploaded bil  */}
      {/* <ImageCarousel
        images={selectedBill}
        isOpen={isSelectedBillPreviewOpen}
        onClose={onSelectedBillPreviewClose}
        initialIndex={carouselStartIndex}
        showFooterActionButtons={false}
      /> */}

      <AppAlertWithIconDialog
        open={dialogOpen.open}
        iconType={dialogOpen.iconType}
        message={dialogOpen.message}
        subMessage={dialogOpen.subMessage}
        onOpenChange={(open) => setIsDialogOpen((prev) => ({ ...prev, open }))}
      />

      <BalanceBillUploadDialog
        open={confirmationDialogOpen}
        selectedBill={selectedImages}
        onOpenChange={(open) => {
          setSelectedImages([]);
          setCarouselStartIndex(0);
          setConfirmationDialogOpen(open);
        }}
      />

      <BalanceBillDocumentPreviewDialog
        open={billItemDialogOpen}
        onOpenChange={(open: boolean) => {
          setBillItemDialogOpen(open);
        }}
        billDetails={balanceBillItemResponseData}
      />
    </>
  );
};

export default BalanceBill;
